/**
 * Similar to the structure of ../../JobDrawer
 * The hierarchy of components here is:
 * CreateTriggerDrawer - thin wrapper to allow animated drawer transition without loading content
 * CreateTriggerDrawerInner - queries for data, shows loading indicator
 * CreateTriggerDrawerLoaded - UI the user interacts with
 */
// @todo: Migrate to 'Dialog.Drawer' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import Drawer from '@material-ui/core/Drawer';
import React, {useCallback, useContext, useEffect, useState} from 'react';
import {useShallow} from 'zustand/react/shallow';

import {envIsPublicCloud} from '../../../../config';
import {useHandleScroll} from '../../../../pages/HomePage/HomePageSidebar/useHandleScroll';
import {DEFAULT_ONPREM_ORGANIZATION_NAME} from '../../../../pages/OrgDashboard/OrgRoles/RolesDashboard';
import {useRampFlagMetricChangeNotifications} from '../../../../util/rampFeatureFlags';
import {ResizableDrawer} from '../../../common/ResizableDrawer/ResizableDrawer';
import {AutomationsIntercomChatButton} from '../../../IntercomChatButton';
import {AccountSelectorContext} from '../../../Search/SearchNav/AccountSelectorContextProvider';
import {useActionConfigStore} from './ActionStore';
import {AutomationStoreActions, ResetStoresCallback} from './AutomationStore';
import * as S from './CreateTriggerDrawer.styles';
import {CreateTriggerDrawerLoaded} from './CreateTriggerDrawerLoaded';
import {CreateTriggerDrawerInit, EventStoreActions} from './EventStore';

type CreateTriggerDrawerProps = {
  open: boolean;
  closeDrawer: () => void;
  createTriggerDrawerInit: CreateTriggerDrawerInit;
  refetchProjectTriggers: () => Promise<any>;
  refetchCollectionTriggers?: () => Promise<any>;
  showIntercom?: boolean;
};

export const getResetStores =
  (
    init: CreateTriggerDrawerInit,
    metricChangeNotificationsEnabled: boolean,
    onCloseInternal: () => void,
    doAsyncInit: (
      metricChangeNotificationsEnabled: boolean,
      init: CreateTriggerDrawerInit,
      onCloseInternal: () => void
    ) => Promise<void>
  ): ResetStoresCallback =>
  () => {
    EventStoreActions.initAsync(init, metricChangeNotificationsEnabled);
    // Reset all stores
    doAsyncInit(metricChangeNotificationsEnabled, init, onCloseInternal);
  };

export const CreateTriggerDrawer = ({
  refetchProjectTriggers,
  refetchCollectionTriggers,
  createTriggerDrawerInit,
  open,
  closeDrawer,
  showIntercom = true,
}: CreateTriggerDrawerProps) => {
  const {scrollHeight} = useHandleScroll();
  const [width, setWidth] = useState(600); // Remember last size
  const onSetWidth = (newWidth: number) => {
    setWidth(newWidth);
  };

  const onCloseInternal = useCallback(() => {
    closeDrawer();
  }, [closeDrawer]);

  const {selectedAccount} = useContext(AccountSelectorContext);

  const orgName = envIsPublicCloud
    ? (selectedAccount?.name ?? '')
    : DEFAULT_ONPREM_ORGANIZATION_NAME;

  const {doAsyncInit} = useActionConfigStore(useShallow(s => s));
  const {
    data: metricChangeNotificationsEnabled,
    loading: metricChangeNotificationsLoading,
  } = useRampFlagMetricChangeNotifications(
    orgName,
    createTriggerDrawerInit.eventEntityName ?? ''
  );
  useEffect(() => {
    // This useEffect ensures that we reset action config each time the drawer opens
    if (open && !metricChangeNotificationsLoading) {
      // note that we are calling an async function here, but not await'ing
      // This is on purpose - later code in this function does not rely on this running to completion.
      const resetStores = getResetStores(
        createTriggerDrawerInit,
        metricChangeNotificationsEnabled,
        onCloseInternal,
        doAsyncInit
      );
      resetStores();
      AutomationStoreActions.init(createTriggerDrawerInit, resetStores);
    }
    // we should only trigger this when open changes, so ignoring changes to the rest of the
    // dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    metricChangeNotificationsLoading,
    metricChangeNotificationsEnabled,
    // in the workspace panel, we want to allow users to change the suggested metric name
    // by selecting different panels, so allow this property to cause us to run the init functions
    createTriggerDrawerInit.suggestedMetricName,
  ]);

  return (
    // Note: Setting `disableEnforceFocus={true}` is a workaround that ensures the open drawer does not steal focus
    // from Intercom chat widget.  Otherwise, the viewer cannot edit new messages in the Intercom chat app,
    // should they wish to do so.
    //
    // For more info, see:
    // - https://github.com/mui/material-ui/issues/17497
    // - https://wandb.atlassian.net/browse/WB-19621
    <Drawer
      anchor="right"
      open={open}
      onClose={onCloseInternal}
      disableEnforceFocus={true}>
      <ResizableDrawer
        startWidth={width}
        minWidth={600}
        onSetWidth={onSetWidth}>
        <S.DrawerContents scrollHeight={scrollHeight}>
          <CreateTriggerDrawerLoaded
            onClose={onCloseInternal}
            refetchProjectTriggers={refetchProjectTriggers}
            refetchCollectionTriggers={refetchCollectionTriggers}
          />
        </S.DrawerContents>
        {showIntercom ? <AutomationsIntercomChatButton /> : <></>}
      </ResizableDrawer>
    </Drawer>
  );
};
